import { PagingParameters } from "@/api/dtos";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabsContained.tsx";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import {
  tiragesColumns,
  tiragesColumnsVisibility,
} from "@/config/dashboard/tirages";
import i18n, { SupportedLocale } from "@/i18n";
import { computeFieldTirageDimensions } from "@/lib/computeFields";
import { getDefaultImage } from "@/lib/image";
import { Tirage } from "@/lib/types";
import { parseJsonOrUndefined } from "@/lib/utils";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import {
  ChevronLeft,
  LayoutGrid,
  PlusCircle,
  PlusIcon,
  Table2,
  Trash2Icon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";

const STORAGE_KEY = "tirage-links-table-preferences";

interface TirageLinksProps {
  id: string;
  type: LinkableType;
}

/**
 * TirageLinks component
 * @returns {JSX.Element}
 */
const TirageLinks = ({ id, type }: TirageLinksProps) => {
  const { t } = useTranslation();

  const [getPaginationRelatedTirages, setPaginationRelatedTirages] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: tiragesRelated } = useGetRelatedFields(
    id,
    type,
    "relatedTirage",
    getPaginationRelatedTirages.pageSize,
    getPaginationRelatedTirages.page * getPaginationRelatedTirages.pageSize,
  );

  const { data: countRelatedTirages } = useCountRelatedFields(id, type, [
    "relatedTirage",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedTirage",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedTirage",
  );

  const [isAddMode, setIsAddMode] = useState(false);
  const [searchContent, setSearchContent] = useState("");

  const [getPaginationTiragesToAdd, setPaginationTiragesToAdd] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const { data: notAlreadyRelatedTirages } = useGetNotRelatedResource<Tirage>({
    resultType: "tirage",
    resourceId: id,
    pagination: getPaginationTiragesToAdd,
    searchQuery: searchContent,
  });

  const columns: ColumnDef<Tirage, any>[] = useMemo(() => {
    const handleCreateRelatedTirages = (tirageId: string) => {
      try {
        createRelatedFields([id, tirageId]);
      } catch (error) {
        console.error(error);
      }
    };

    const handleDeleteRelation = (tirageId: string) => {
      deleteRelation(tirageId);
    };

    return [
      ...generateTable(tiragesColumns, "tirage"),
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Tirage> }) => {
          if (isAddMode) {
            return (
              <div className="flex items-center gap-1 justify-end mr-2">
                <ShowElements
                  type="tirage"
                  elements={[
                    {
                      label: t("tirageDescriptionNumeroEnregistrement"),
                      value:
                        row.original.tirageDescriptionNumeroEnregistrement ??
                        "",
                      type: "text",
                    },
                    {
                      label: t("tirageDescriptionNumeroInventaire"),
                      value:
                        row.original.tirageDescriptionNumeroInventaire ?? "",
                      type: "text",
                    },
                    {
                      label: t("tirageDescriptionNotes"),
                      value: row.original.tirageDescriptionNotes ?? "",
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.original.attachments ?? [],
                      type: "image",
                    },
                  ]}
                  onAdd={() => handleCreateRelatedTirages(row.original.id)}
                />
                <Button
                  className="font-semibold flex gap-2 p-1"
                  variant="ghost"
                  onClick={() => handleCreateRelatedTirages(row.original.id)}
                  type="button"
                >
                  <PlusCircle className="w-5 h-5 cursor-pointer text-[#005D8D]" />
                </Button>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(row.original.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <ShowElements
                type="tirage"
                elements={[
                  {
                    label: t("tirageDescriptionNumeroEnregistrement"),
                    value:
                      row.original.tirageDescriptionNumeroEnregistrement ?? "",
                    type: "text",
                  },
                  {
                    label: t("tirageDescriptionNumeroInventaire"),
                    value: row.original.tirageDescriptionNumeroInventaire ?? "",
                    type: "text",
                  },
                  {
                    label: t("tirageDescriptionNotes"),
                    value: row.original.tirageDescriptionNotes ?? "",
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments ?? [],
                    type: "image",
                  },
                ]}
              />
            </div>
          );
        },
      },
    ];
  }, [isAddMode, t, createRelatedFields, deleteRelation, id]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { visibility } = JSON.parse(saved);
        return (
          visibility || {
            ...tiragesColumnsVisibility,
            vignette: true,
            tiragePublicationStatus: false,
          }
        );
      }
      return {
        ...tiragesColumnsVisibility,
        vignette: true,
        tiragePublicationStatus: false,
      };
    },
  );

  const flatVisibleTirageColumns = tiragesColumns.flatMap(
    (column: ColumnFormatted) => (column.visible ? column.key : []),
  );

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const { order } = JSON.parse(saved);
      return order || flatVisibleTirageColumns;
    }
    return flatVisibleTirageColumns;
  });

  return (
    <div className="w-full">
      <Tabs defaultValue="list">
        {isAddMode && (
          <div
            className="flex items-center gap-1 mb-4 cursor-pointer"
            onClick={handleAddMode}
          >
            <ChevronLeft className="w-3 h-3 text-gray-300" />
            <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
              {t("linkedEntries")}
            </p>
          </div>
        )}
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-semibold">{t("linkedTirages")}</h2>
          {!isAddMode && (
            <div className="flex gap-2 items-center">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="grid">
                  <LayoutGrid className="h-4 w-4" />
                </TabsTrigger>
                <TabsTrigger value="list">
                  <Table2 className="h-4 w-4" />
                </TabsTrigger>
              </TabsList>
              <Button variant="outline" onClick={handleAddMode}>
                <PlusIcon className="w-4 h-4 mr-2" />
                {t("add")}
              </Button>
            </div>
          )}
          {isAddMode && (
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          )}
        </div>

        {isAddMode && (
          <DataTable
            columns={columns}
            data={notAlreadyRelatedTirages?.data ?? []}
            pagination={{
              pagination: getPaginationTiragesToAdd,
              setPage: setPaginationTiragesToAdd,
              totalSize: notAlreadyRelatedTirages?.totalSize ?? 0,
            }}
            storageKey={STORAGE_KEY}
            options={{
              enableColumnResizing: true,
              onColumnVisibilityChange: setColumnVisibility,
              onColumnOrderChange: setColumnsOrder,
              state: {
                columnVisibility: columnVisibility,
                columnOrder: columnsOrder,
              },
            }}
          />
        )}
        <TabsContent value="list">
          {!isAddMode && (
            <DataTable
              columns={columns}
              data={tiragesRelated ?? []}
              pagination={{
                pagination: getPaginationRelatedTirages,
                setPage: setPaginationRelatedTirages,
                totalSize: countRelatedTirages?.relatedTirage,
              }}
              storageKey={STORAGE_KEY}
              options={{
                enableColumnResizing: true,
                onColumnVisibilityChange: setColumnVisibility,
                onColumnOrderChange: setColumnsOrder,
                state: {
                  columnVisibility: columnVisibility,
                  columnOrder: columnsOrder,
                },
              }}
            />
          )}
        </TabsContent>
        <TabsContent value="grid">
          <div className="w-full flex gap-8 flex-wrap">
            {!isAddMode &&
              tiragesRelated?.map((tirage: Tirage) => {
                return (
                  <div
                    key={tirage.id}
                    className={`h-[389px] flex-col justify-start items-start inline-flex max-w-[300px]`}
                  >
                    <img
                      className="h-[251px] relative"
                      src={
                        getDefaultImage(tirage.attachments ?? [], "tirage") ||
                        ""
                      }
                      alt="image illustration"
                    />
                    <div className="self-stretch h-[30px] py-2 flex-col justify-start items-start gap-1 flex">
                      <div>
                        <span className="text-slate-600 text-xs font-normal font-['Instrument Sans'] leading-[14.40px]">
                          N° d'enregistrement{" "}
                        </span>
                        <span className="text-[#090909] text-xs text-left font-semibold font-['Instrument Sans'] leading-[14.40px]">
                          {tirage.tirageDescriptionNumeroEnregistrement}
                        </span>
                      </div>
                    </div>
                    <div className="self-stretch h-[108px] py-2 flex-col justify-start items-start gap-1 flex">
                      {tirage.tirageDescriptionType &&
                        Object.keys(tirage.tirageDescriptionType).length >
                          0 && (
                          <p className="text-start self-stretch text-[#090909] text-xs font-normal font-['Instrument Sans'] leading-[14.40px]">
                            {tirage.tirageDescriptionType[
                              i18n.language as SupportedLocale
                            ] || ""}
                          </p>
                        )}
                      <p className="text-left leading-tight">
                        <span className="text-[#090909] w-full text-base text-left font-semibold font-['Instrument Sans'] leading-tight">
                          {tirage.tirageDescriptionNumeroInventaire || ""}
                        </span>
                        {tirage.tirageDescriptionDateCreation && (
                          <span className="text-[#090909] text-base text-left font-normal font-['Instrument Sans'] leading-tight">
                            {", "}
                            {tirage.tirageDescriptionDateCreation}
                          </span>
                        )}
                      </p>
                      {tirage.tirageDescriptionDimensionsWithMargins && (
                        <div
                          className="text-[#090909] text-xs font-normal font-['Instrument Sans'] leading-[14.40px]"
                          dangerouslySetInnerHTML={{
                            __html: computeFieldTirageDimensions(
                              {
                                unknownDimensions: false,
                                ...(parseJsonOrUndefined(
                                  tirage.tirageDescriptionDimensionsWithMargins,
                                ) ?? {}),
                              },
                              {
                                language: i18n.language as SupportedLocale,
                              },
                            ),
                          }}
                        ></div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TirageLinks;
